import * as THREE from "three";

/**
 * One-Euro filter (Casiez, Roussel & Vogel, CHI 2012).
 *
 * Raw landmarks wobble by a pixel or two every frame even on a perfectly still
 * head, which reads as the glasses vibrating. A plain low-pass fixes the
 * shake but adds lag you notice the moment you turn your head.
 *
 * One-Euro adapts instead: it filters hard when the signal is slow (killing
 * jitter while stationary) and barely at all when the signal is fast (keeping
 * fast head turns responsive). `beta` sets how aggressively it trades one for
 * the other.
 */
export class OneEuroFilter {
  #previous = null;
  #previousDerivative = 0;
  #previousTime = null;

  /**
   * @param {number} minCutoff  Cutoff at zero speed. Lower = steadier when still, but laggier.
   * @param {number} beta  How much speed raises the cutoff. Higher = less lag when moving fast.
   * @param {number} derivativeCutoff  Cutoff for the speed estimate itself.
   */
  constructor(minCutoff = 1.2, beta = 0.02, derivativeCutoff = 1) {
    this.minCutoff = minCutoff;
    this.beta = beta;
    this.derivativeCutoff = derivativeCutoff;
  }

  filter(value, timestampMs) {
    if (this.#previous === null || this.#previousTime === null) {
      this.#previous = value;
      this.#previousTime = timestampMs;
      return value;
    }

    const dt = (timestampMs - this.#previousTime) / 1000;
    // Guard against a zero/negative delta from duplicated or reordered frames.
    const rate = dt > 0 ? 1 / dt : 60;

    const derivative = (value - this.#previous) * rate;
    const smoothedDerivative = lowPass(
      derivative,
      this.#previousDerivative,
      alpha(this.derivativeCutoff, rate),
    );

    const cutoff = this.minCutoff + this.beta * Math.abs(smoothedDerivative);
    const filtered = lowPass(value, this.#previous, alpha(cutoff, rate));

    this.#previous = filtered;
    this.#previousDerivative = smoothedDerivative;
    this.#previousTime = timestampMs;
    return filtered;
  }

  reset() {
    this.#previous = null;
    this.#previousDerivative = 0;
    this.#previousTime = null;
  }
}

function alpha(cutoff, rate) {
  const tau = 1 / (2 * Math.PI * cutoff);
  const dt = 1 / rate;
  return 1 / (1 + tau / dt);
}

function lowPass(value, previous, a) {
  return a * value + (1 - a) * previous;
}

/** Three independent One-Euro filters, one per axis. */
export class Vector3Filter {
  constructor(minCutoff, beta) {
    this.x = new OneEuroFilter(minCutoff, beta);
    this.y = new OneEuroFilter(minCutoff, beta);
    this.z = new OneEuroFilter(minCutoff, beta);
  }

  filter(value, timestampMs, target) {
    return target.set(
      this.x.filter(value.x, timestampMs),
      this.y.filter(value.y, timestampMs),
      this.z.filter(value.z, timestampMs),
    );
  }

  reset() {
    this.x.reset();
    this.y.reset();
    this.z.reset();
  }
}

/**
 * Rotation is smoothed by slerping toward the target instead of filtering
 * components, which would denormalise the quaternion. The fixed rate is frame
 * -rate compensated so it behaves the same at 30 and 60 fps.
 */
export class QuaternionSmoother {
  #initialised = false;
  #current = new THREE.Quaternion();

  constructor(responsiveness = 18) {
    this.responsiveness = responsiveness;
  }

  filter(value, deltaSeconds, target) {
    if (!this.#initialised) {
      this.#current.copy(value);
      this.#initialised = true;
    } else {
      // Quaternions are double-covered: q and -q are the same rotation, but
      // slerping to the far one takes the long way round and spins the model.
      if (this.#current.dot(value) < 0) {
        value.set(-value.x, -value.y, -value.z, -value.w);
      }
      const t = 1 - Math.exp(-this.responsiveness * deltaSeconds);
      this.#current.slerp(value, t);
    }
    return target.copy(this.#current);
  }

  reset() {
    this.#initialised = false;
  }
}
